import type { Doc } from "building-types/doc";
import type { SolidFloorAssemblyOverride } from "building-types/floors/floorAssemblyTypes";
import { matchBaysByInteriorPoint, splitLinesIntersectingPerimeter } from "building-types/floors/floorBayUtils";
import type { FloorBay, FloorSplitData } from "building-types/floors/floorSplitDataTypes";
import type { Table } from "common/data/table";
import { setIn } from "common/utils/objectUtils";
import type { PolyLine2 } from "geom/polyLine2Types";
import { docGetShape } from "../../docGetShape";
import {
  getStoredFloorSplitData,
  isHorizontalFloorAssemblyRoomDerived,
  resolveRoomDerivedRoomIds,
} from "./floorAssembly";
import { roomOutlinesToUnionPerimeters, splitPerimeterBySplitLines } from "./floorPerimeterGeometry";
import { floorSplitDataWithReconciledBays } from "./floorSplitDataTools";

export function reconcileBaysForRoomOutlines(
  roomOutlines: PolyLine2[],
  existingSplitData: FloorSplitData,
): Table<FloorBay> {
  const validOutlines = roomOutlines.filter((o) => o.length >= 6);
  const perimeters = roomOutlinesToUnionPerimeters(validOutlines);
  if (perimeters.length === 0) {
    return {};
  }

  const allSplitLines = Object.values(existingSplitData.splitLines ?? {});

  if (allSplitLines.length === 0) {
    return {};
  }

  // Build the final list of bay polygons by splitting perimeters that have
  // intersecting split lines, mirroring what createBaysFromPerimeters does
  // in building generation.
  const bayPolygons: PolyLine2[] = [];
  for (const perimeter of perimeters) {
    if (allSplitLines.length > 0) {
      const relevant = splitLinesIntersectingPerimeter(perimeter, allSplitLines);
      if (relevant.length > 0) {
        bayPolygons.push(...splitPerimeterBySplitLines(perimeter, relevant));
        continue;
      }
    }
    bayPolygons.push(perimeter);
  }

  // Match existing bays to the computed polygons by interior point containment.
  const { updatedBays } = matchBaysByInteriorPoint(bayPolygons, existingSplitData.bays ?? {}, false, 0);

  // Nuke edge overrides on all bays (room changes invalidate edge geometry)
  // but preserve assembly overrides.
  const result: Record<string, FloorBay> = {};
  for (const [bayId, bay] of Object.entries(updatedBays)) {
    result[bayId] = {
      ...bay,
      bayEdgeOverrides: undefined,
    };
  }

  return result;
}

export function collectRoomOutlinesFromDoc(doc: Doc, roomIds: readonly string[]): PolyLine2[] {
  const outlines: PolyLine2[] = [];
  for (const roomId of roomIds) {
    const outline = docGetShape(doc, {
      type: "SHAPE",
      parent: { type: "ROOM", id: roomId },
      shapeId: "outline",
    });
    if (outline && outline.length >= 6) {
      outlines.push(outline);
    }
  }
  return outlines;
}

export function reconcileFloorBaysAfterRoomDeletion(doc: Doc): Doc {
  const floors = doc.floors;
  if (!floors) {
    return doc;
  }

  for (const floorId in floors) {
    const floor = floors[floorId] as SolidFloorAssemblyOverride;
    if (!isHorizontalFloorAssemblyRoomDerived(floor)) {
      continue;
    }

    const storedSplitData = getStoredFloorSplitData(floor);
    if (!storedSplitData) {
      continue;
    }

    const roomIds = resolveRoomDerivedRoomIds(doc, floor);
    if (roomIds.length === 0) {
      doc = setIn(doc, ["floors", floorId, "splitData"], undefined);
      continue;
    }

    const roomOutlines = collectRoomOutlinesFromDoc(doc, roomIds);
    const bays = reconcileBaysForRoomOutlines(roomOutlines, storedSplitData);
    const normalizedSplitData = floorSplitDataWithReconciledBays(storedSplitData, bays);
    doc = setIn(doc, ["floors", floorId, "splitData"], normalizedSplitData);
  }

  return doc;
}
